import { useState } from 'react'
import { Box, Typography } from '@mui/material'
import { Gavel } from '@mui/icons-material'

/** Đường dẫn static (Vite `public/docs/`). */
const UEL_LOGO_SRC = '/docs/uel_logo_2026-01.png'
const TBS_LOGO_SRC = '/docs/logo-tbs.png'

/** Dòng 1–2: tên dự án đầy đủ (hiển thị header / sidebar / topbar). */
export const BRAND_TITLE_LINE1 = 'HỆ THỐNG TÒA ÁN ĐIỆN TỬ THỰC NGHIỆM TBS - UEL'
export const BRAND_TITLE_LINE2 = 'TUBES (TBS-UEL Bench E-Court System)'

/** Alias ngắn (footer / chỗ cần một chuỗi) */
export const BRAND_NAME = BRAND_TITLE_LINE2

interface BrandLogoMarksProps {
  /** Chiều cao logo (px). */
  height: number
  /** Nền sidebar tối: thêm nền sáng nhẹ để logo TBS (xanh lá) đọc rõ. */
  onDark?: boolean
  /** Sidebar hẹp: giảm chồng lệch để gọn chiều ngang. */
  tightOverlap?: boolean
}

/** Hai logo UEL + TBS chồng lệch (lồng) nhau theo trục ngang. */
function BrandLogoMarks({ height, onDark, tightOverlap }: BrandLogoMarksProps) {
  const [uelErr, setUelErr] = useState(false)
  const [tbsErr, setTbsErr] = useState(false)

  if (uelErr && tbsErr) {
    return (
      <Box
        sx={{
          width: height,
          height,
          borderRadius: 2,
          bgcolor: onDark ? 'rgba(255,255,255,0.2)' : 'primary.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Gavel sx={{ color: 'white', fontSize: height * 0.55 }} />
      </Box>
    )
  }

  const inner = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        height,
        flexShrink: 0,
      }}
      aria-hidden
    >
      {!uelErr && (
        <Box
          component="img"
          src={UEL_LOGO_SRC}
          alt=""
          onError={() => setUelErr(true)}
          sx={{ height, width: 'auto', display: 'block', zIndex: 1, position: 'relative' }}
        />
      )}
      {!tbsErr && (
        <Box
          component="img"
          src={TBS_LOGO_SRC}
          alt=""
          onError={() => setTbsErr(true)}
          sx={{
            height: height * (tightOverlap ? 0.88 : 0.92),
            width: 'auto',
            ml: tightOverlap ? -0.75 : -1.25,
            zIndex: 2,
            position: 'relative',
            alignSelf: 'center',
          }}
        />
      )}
    </Box>
  )

  if (!onDark) return inner

  return (
    <Box
      sx={{
        px: 0.75,
        py: 0.35,
        borderRadius: 2,
        bgcolor: 'rgba(255,255,255,0.96)',
        display: 'flex',
        alignItems: 'center',
        boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
      }}
    >
      {inner}
    </Box>
  )
}

interface LogoProps {
  variant?: 'sidebar' | 'header' | 'compact' | 'topbar'
  showText?: boolean
}

export default function Logo({ variant = 'sidebar', showText = true }: LogoProps) {
  if (variant === 'compact' || variant === 'topbar') {
    const onDark = variant === 'topbar'
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          minWidth: 0,
          flexWrap: 'wrap',
          rowGap: 0.75,
        }}
      >
        <BrandLogoMarks height={40} onDark={onDark} tightOverlap />
        {showText && (
          <Box sx={{ minWidth: 0, flex: { xs: '1 1 160px', sm: '0 1 auto' }, maxWidth: { sm: 420 } }}>
            <Typography
              component="div"
              sx={{
                fontWeight: 700,
                color: onDark ? '#fff' : 'primary.main',
                fontSize: onDark ? '0.72rem' : '0.8rem',
                lineHeight: 1.3,
                wordBreak: 'break-word',
              }}
            >
              {BRAND_TITLE_LINE1}
            </Typography>
            <Typography
              component="div"
              sx={{
                fontWeight: 600,
                color: onDark ? 'rgba(255,255,255,0.92)' : 'text.secondary',
                fontSize: onDark ? '0.65rem' : '0.72rem',
                lineHeight: 1.35,
                mt: 0.35,
                wordBreak: 'break-word',
              }}
            >
              {BRAND_TITLE_LINE2}
            </Typography>
          </Box>
        )}
      </Box>
    )
  }

  if (variant === 'header') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          gap: { xs: 1, sm: 1.5 },
          textAlign: { xs: 'center', sm: 'left' },
          width: '100%',
        }}
      >
        <BrandLogoMarks height={36} onDark={false} tightOverlap />
        {showText && (
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="div"
              sx={{ fontWeight: 700, lineHeight: 1.25, color: 'primary.main', wordBreak: 'break-word', fontSize: { xs: '0.82rem', sm: '0.9rem' } }}
            >
              {BRAND_TITLE_LINE1}
            </Typography>
            <Typography
              component="div"
              sx={{ color: 'text.secondary', fontSize: { xs: '0.75rem', sm: '0.8rem' }, fontWeight: 600, display: 'block', mt: 0.5, lineHeight: 1.35, wordBreak: 'break-word' }}
            >
              {BRAND_TITLE_LINE2}
            </Typography>
          </Box>
        )}
      </Box>
    )
  }

  /* Sidebar (~220px): xếp dọc — tránh logo 2 lớp chiếm hết chiều ngang làm mất chữ */
  return (
    <Box
      sx={{
        px: 1.75,
        py: 2,
        bgcolor: 'transparent',
        color: 'rgba(255, 255, 255, 0.95)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1.25,
        textAlign: 'center',
      }}
    >
      <BrandLogoMarks height={42} onDark={true} tightOverlap />
      {showText && (
        <Box sx={{ width: '100%', px: 0.25 }}>
          <Typography
            component="div"
            sx={{
              fontWeight: 700,
              color: 'white',
              lineHeight: 1.35,
              /* Nhỏ hơn 1 bậc so với menu — giúp dòng 1 gói ~2 hàng trong sidebar ~220px */
              fontSize: '0.8125rem',
              letterSpacing: '0.01em',
              wordBreak: 'break-word',
              hyphens: 'auto',
            }}
          >
            {BRAND_TITLE_LINE1}
          </Typography>
          <Typography
            component="div"
            sx={{
              color: 'rgba(255, 255, 255, 0.92)',
              fontWeight: 600,
              fontSize: '0.75rem',
              mt: 0.45,
              lineHeight: 1.35,
              wordBreak: 'break-word',
            }}
          >
            {BRAND_TITLE_LINE2}
          </Typography>
        </Box>
      )}
    </Box>
  )
}
